import { useCallback, useEffect } from 'react';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { ReleaseResolution } from '../wizards/common/releaseResolutionTypes';
import { CreationPageType } from './inferPageType';
import { CreationSource, CreationStage } from './types';
import { CreationDraft } from './props';
import { ManualFields } from './CreationManualStage';
import { CreationTemplateId, templateBlocks } from './blankTemplates';
import { CreatedSheetPage, useCreateFromSheet } from './useCreateFromSheet';
import { useWizardPublishReport } from './useWizardPublishReport';

interface UseCreationSubmitProps {
  accountId: number;
  stage: CreationStage;
  source: CreationSource;
  draft: CreationDraft;
  resolution: ReleaseResolution;
  catalogRow?: CatalogItem;
  manualFields: ManualFields;
  blankName: string;
  template: CreationTemplateId;
  templateHeadings: { tour: string; merch: string };
  closeModal: () => void;
  /**
   * Publish the page at once: the sheet was opened from the add-song flow (`&from=addSong`, owner
   * decision 6). False everywhere else: the page is a draft, as before.
   */
  publishNow: boolean;
}

/**
 * Pressing Create.
 *
 * Which endpoint runs is decided by what the sheet concluded, not by anything
 * the artist was asked. Wherever it lands, the artist ends up on the Creator
 * for the page they just made — the same place both wizards always left them.
 */
export function useCreationSubmit({
  accountId,
  stage,
  source,
  draft,
  resolution,
  catalogRow,
  manualFields,
  blankName,
  template,
  templateHeadings,
  closeModal,
  publishNow,
}: UseCreationSubmitProps) {
  const router = useSMLRouter();
  const { createReleasePage, createPresavePage, createBlankPage, isCreating, error, isSlugRejected } =
    useCreateFromSheet(accountId);
  const { markTaken } = draft.slug;
  const reportPublish = useWizardPublishReport();
  // Sent only when asked; the other callers keep `publish` absent.
  const publish = publishNow ? true : undefined;

  // The server checked the address again and disagreed with our check. Say so
  // beside the field rather than as an error the artist cannot place.
  useEffect(() => {
    if (isSlugRejected) {
      markTaken();
    }
  }, [isSlugRejected, markTaken]);

  const goToPage = useCallback(
    (smartLinkId: number | undefined) => {
      if (smartLinkId === undefined) {
        return;
      }
      closeModal();
      router.popToRoot();
      router.push(SMLRoutes.details, { accountId, smartLinkId });
    },
    [accountId, closeModal, router]
  );

  const goToCreated = useCallback(
    (created: CreatedSheetPage | undefined) => {
      if (created === undefined) {
        return;
      }
      goToPage(created.smartLinkId);
      reportPublish(created.publishResult, created.shareUrl);
    },
    [goToPage, reportPublish]
  );

  const slugValue = draft.slug.value === '' ? undefined : draft.slug.value;
  // Only sent when the artist moved the page off the account's host. Left out,
  // the page follows the account instead of freezing today's answer.
  const platformDomain = draft.platformDomainOverride ?? undefined;
  const catalogItemUuid = catalogRow?.kind === CatalogItemKind.unreleased ? catalogRow.uuid : undefined;
  // The sheet reads the row live from the catalog cache, so a cover made in the cover editor
  // (another tab) goes on the page too.
  // A cover the artist uploaded onto the row. It is a file we already hold, so
  // the page points at it rather than at a url the server would have to fetch —
  // and a relative file url is one it could not fetch at all.
  const artworkFileId = catalogRow?.artworkFile?.id;

  const submit = useCallback(() => {
    if (stage === CreationStage.blank) {
      createBlankPage({
        name: blankName,
        slug: draft.slug.value,
        theme: draft.theme,
        platformDomain,
        blocks: templateBlocks(template, templateHeadings),
      })
        .then((smartLinkId) => {
          goToPage(smartLinkId);
          return SMLAnalytics.trackSmartLinkCreated('custom');
        })
        .catch(console.error);
      return;
    }

    if (stage === CreationStage.manual) {
      createPresavePage({
        title: manualFields.title,
        artistName: manualFields.artistName,
        releaseDate: draft.releaseDate,
        slug: slugValue,
        theme: draft.theme,
        platformDomain,
        newCatalogItem: { releaseType: manualFields.releaseType === '' ? undefined : manualFields.releaseType },
        publish,
      })
        .then((created) => {
          goToCreated(created);
          return SMLAnalytics.trackSmartLinkCreated('presave');
        })
        .catch(console.error);
      return;
    }

    const trackMaid = source.kind === 'catalogTrack' ? source.trackMaid : (catalogRow?.catalogTrackMaid ?? undefined);

    if (draft.pageType === CreationPageType.presave) {
      createPresavePage({
        title: resolution.title ?? catalogRow?.title ?? '',
        artistName: resolution.artistName ?? catalogRow?.artistName ?? '',
        releaseDate: draft.releaseDate,
        imageId: artworkFileId,
        imageURL:
          artworkFileId === undefined ? (catalogRow?.artworkUrl ?? resolution.imageUrl ?? undefined) : undefined,
        catalogTrackMaid: trackMaid,
        catalogItemUuid,
        slug: slugValue,
        theme: draft.theme,
        platformDomain,
        publish,
      })
        .then((created) => {
          goToCreated(created);
          return SMLAnalytics.trackSmartLinkCreated('presave');
        })
        .catch(console.error);
      return;
    }

    if (resolution.main === undefined) {
      return;
    }
    createReleasePage({
      main: resolution.main,
      others: resolution.others,
      imageId: artworkFileId,
      catalogTrackMaid: trackMaid,
      slug: slugValue,
      theme: draft.theme,
      platformDomain,
      publish,
    })
      .then((created) => {
        goToCreated(created);
        return SMLAnalytics.trackSmartLinkCreated('release');
      })
      .catch(console.error);
  }, [
    artworkFileId,
    blankName,
    catalogItemUuid,
    catalogRow,
    createBlankPage,
    createPresavePage,
    createReleasePage,
    draft.pageType,
    draft.releaseDate,
    draft.slug.value,
    draft.theme,
    goToCreated,
    goToPage,
    manualFields,
    platformDomain,
    publish,
    resolution,
    slugValue,
    source,
    stage,
    template,
    templateHeadings,
  ]);

  return { submit, isCreating, error };
}
